import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBadge from '../components/StatusBadge';
import { 
  PlusIcon, 
  CheckIcon, 
  XMarkIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  FunnelIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  PrinterIcon,
  TagIcon,
  ShoppingBagIcon,
  CalculatorIcon,
  ClipboardDocumentListIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format, subDays } from 'date-fns';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const Sales = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [showOrderConsignmentsModal, setShowOrderConsignmentsModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [pendingOrderConsignments, setPendingOrderConsignments] = useState([]);
  const [processingConsignment, setProcessingConsignment] = useState(null);
  const [processingSale, setProcessingSale] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    due_date: '',
    payment_method: 'cash',
    reference_number: '',
    notes: ''
  });
  const [filters, setFilters] = useState({
    status: '',
    shop_owner_id: '',
    sales_agent_id: '',
    from_date: subDays(new Date(), 30),
    to_date: new Date(),
    payment_type: ''
  });
  const [shopOwners, setShopOwners] = useState([]);
  const [salesAgents, setSalesAgents] = useState([]);
  const [summary, setSummary] = useState({
    total_sales: 0,
    total_collected: 0,
    outstanding: 0,
    cash_sales: 0,
    credit_sales: 0,
    total_discount: 0,
    total_commission: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 5,
    total: 0
  });

  useEffect(() => {
    fetchTransactions();
    fetchFilters();
    if (showOrderConsignmentsModal) {
      fetchPendingOrderConsignments();
    }
  }, [pagination.page, pagination.per_page, filters, showOrderConsignmentsModal]);

  // Separate useEffect for initial fetch when component mounts
  useEffect(() => {
    fetchPendingOrderConsignments();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        status: filters.status || undefined,
        shop_owner_id: filters.shop_owner_id || undefined,
        sales_agent_id: filters.sales_agent_id || undefined,
        payment_type: filters.payment_type || undefined,
        from_date: filters.from_date ? format(filters.from_date, 'yyyy-MM-dd') : undefined,
        to_date: filters.to_date ? format(filters.to_date, 'yyyy-MM-dd') : undefined
      };

      Object.keys(params).forEach(key => 
        params[key] === undefined && delete params[key]
      );

      const response = await api.get('/sales/transactions', { params });
      
      const items = response.data.items || [];
      setTransactions(items);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));

      const summary = {
        total_sales: items.reduce((sum, t) => sum + (t.total_amount || 0), 0),
        total_collected: items.reduce((sum, t) => sum + (t.amount_paid || 0), 0),
        outstanding: items.reduce((sum, t) => sum + (t.balance || 0), 0),
        cash_sales: items.filter(t => t.payment_type === 'cash').reduce((sum, t) => sum + (t.total_amount || 0), 0),
        credit_sales: items.filter(t => t.payment_type === 'credit').reduce((sum, t) => sum + (t.total_amount || 0), 0),
        total_discount: items.reduce((sum, t) => sum + (t.discount || 0), 0),
        total_commission: items.reduce((sum, t) => sum + (t.commission_amount || 0), 0)
      };
      setSummary(summary);
    } catch (error) {
      toast.error('Failed to fetch sales transactions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [ownersRes, agentsRes] = await Promise.all([
        api.get('/users/', { params: { user_type: 'shop_owner', per_page: 1000 } }),
        api.get('/users/', { params: { user_type: 'sales_agent', per_page: 1000 } })
      ]);
      setShopOwners(ownersRes.data.items || []);
      setSalesAgents(agentsRes.data.items || []);
    } catch (error) {
      console.error('Failed to fetch filters:', error);
    }
  };

  const fetchPendingOrderConsignments = async () => {
    try {
      // Fetch consignments that have linked orders but no sales transaction yet
      const response = await api.get('/consignments/', { 
        params: { has_order: true, status: 'issued', per_page: 100 }
      });
      // Filter to only those without sales transactions and not completed
      const pending = (response.data.items || []).filter(c => {
        return c.linked_orders?.some(o => o.status !== 'completed');
      });
      setPendingOrderConsignments(pending);
    } catch (error) {
      console.error('Failed to fetch pending order consignments:', error);
    }
  };

  const handleProcessOrderSale = async (consignment) => {
    setProcessingConsignment(consignment);
    setProcessingSale(true);
    try {
      // Get the linked order - find the first order that is not completed
      const pendingOrder = consignment.linked_orders?.find(o => o.status !== 'completed');
      
      if (!pendingOrder) {
        toast.error('No pending order found for this consignment');
        return;
      }
      
      const response = await api.post(`/orders/${pendingOrder.id}/process-sale`);
      toast.success('Sale processed successfully!');
      
      // Refresh the pending orders list immediately without closing modal
      await fetchPendingOrderConsignments();
      
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to process sale');
    } finally {
      setProcessingSale(false);
      setProcessingConsignment(null);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    
    if (paymentData.amount > selectedTransaction.balance) {
      toast.error('Payment amount cannot exceed balance');
      return;
    }

    try {
      await api.post(`/sales/transactions/${selectedTransaction.id}/payments`, paymentData);
      toast.success('Payment added successfully');
      setShowPaymentModal(false);
      setPaymentData({
        amount: '',
        due_date: '',
        payment_method: 'cash',
        reference_number: '',
        notes: ''
      });
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add payment');
    }
  };

  const handleViewTransaction = (transaction) => {
    navigate(`/sales/${transaction.id}`);
  };

  const handleViewItems = (transaction) => {
    setSelectedTransaction(transaction);
    setShowItemsModal(true);
  };

  const handleExport = async (format) => {
    try {
      const params = {
        format,
        from_date: filters.from_date ? format(filters.from_date, 'yyyy-MM-dd') : undefined,
        to_date: filters.to_date ? format(filters.to_date, 'yyyy-MM-dd') : undefined,
        shop_owner_id: filters.shop_owner_id || undefined,
        sales_agent_id: filters.sales_agent_id || undefined
      };

      const response = await api.get('/reports/sales', {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sales_report.${format === 'excel' ? 'xlsx' : format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Sales report exported successfully');
    } catch (error) {
      toast.error('Failed to export sales report');
    }
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      shop_owner_id: '',
      sales_agent_id: '',
      from_date: subDays(new Date(), 30),
      to_date: new Date(),
      payment_type: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getStatusColor = (status) => {
    const colors = {
      paid: 'bg-green-100 text-green-800',
      partial: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-gray-100 text-gray-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const canProcessOrderSales = () => {
    return user?.user_type === 'sales_agent';
  };

  const columns = [
    {
      header: 'Transaction #',
      accessorKey: 'transaction_number',
      cell: ({ getValue }) => (
        <span className="font-medium text-primary-600">{getValue()}</span>
      )
    },
    {
      header: 'Date',
      accessorKey: 'transaction_date',
      cell: ({ getValue }) => format(new Date(getValue()), 'dd MMM yyyy')
    },
    {
      header: 'Customer',
      accessorKey: 'shop_owner_name',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.shop_owner_name}</div>
          <div className="text-xs text-gray-500">{row.original.sales_agent_name}</div>
        </div>
      )
    },
    {
      header: 'Items',
      cell: ({ row }) => {
        const items = row.original.item_summary || [];
        const itemCount = items.length;
        const firstItem = items[0];
        
        return (
          <div>
            <div className="font-medium">{itemCount} item{itemCount !== 1 ? 's' : ''}</div>
            {firstItem && (
              <div className="text-xs text-gray-500">
                {firstItem.product_name}: {firstItem.quantity_cases} cases
                {itemCount > 1 && ` +${itemCount - 1} more`}
              </div>
            )}
            <button
              onClick={() => handleViewItems(row.original)}
              className="text-xs text-primary-600 hover:text-primary-800 mt-1 inline-flex items-center"
            >
              <ShoppingBagIcon className="h-3 w-3 mr-1" />
              View Details
            </button>
          </div>
        );
      }
    },
    {
      header: 'Subtotal',
      accessorKey: 'subtotal',
      cell: ({ getValue }) => (
        <span className="text-gray-600">ZMW {getValue()?.toLocaleString() || 0}</span>
      )
    },
    {
      header: 'Discount',
      accessorKey: 'discount',
      cell: ({ getValue }) => {
        const discount = getValue() || 0;
        return discount > 0 ? (
          <span className="text-red-600 font-medium flex items-center">
            <TagIcon className="h-4 w-4 mr-1" />
            -ZMW {discount.toLocaleString()}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        );
      }
    },
    {
      header: 'Commission',
      accessorKey: 'commission_amount',
      cell: ({ getValue }) => {
        const commission = getValue() || 0;
        return commission > 0 ? (
          <span className="text-blue-600 font-medium flex items-center">
            <CalculatorIcon className="h-4 w-4 mr-1" />
            ZMW {commission.toLocaleString()}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        );
      }
    },
    {
      header: 'Total',
      accessorKey: 'total_amount',
      cell: ({ getValue }) => (
        <span className="font-bold text-gray-900">ZMW {getValue()?.toLocaleString() || 0}</span>
      )
    },
    {
      header: 'Paid',
      accessorKey: 'amount_paid',
      cell: ({ getValue }) => (
        <span className="text-green-600 font-medium">ZMW {getValue()?.toLocaleString() || 0}</span>
      )
    },
    {
      header: 'Balance',
      accessorKey: 'balance',
      cell: ({ getValue, row }) => {
        const balance = getValue() || 0;
        return (
          <span className={balance > 0 ? 'text-red-600 font-bold' : 'text-green-600'}>
            ZMW {balance.toLocaleString()}
          </span>
        );
      }
    },
    {
      header: 'Type',
      accessorKey: 'payment_type',
      cell: ({ getValue }) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          getValue() === 'cash' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
        }`}>
          {getValue()?.toUpperCase()}
        </span>
      )
    },
    {
      header: 'Due Date',
      accessorKey: 'due_date',
      cell: ({ getValue }) => getValue() ? format(new Date(getValue()), 'dd MMM yyyy') : '-'
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ getValue }) => {
        const status = getValue();
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(status)}`}>
            {status}
          </span>
        );
      }
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewTransaction(row.original)}
            className="text-blue-600 hover:text-blue-900"
            title="View Details"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          {row.original.status !== 'paid' && (
            <button
              onClick={() => {
                setSelectedTransaction(row.original);
                setShowPaymentModal(true);
              }}
              className="text-green-600 hover:text-green-900"
              title="Add Payment"
            >
              <CurrencyDollarIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Sales Transactions</h1>
          <p className="mt-2 text-sm text-gray-700">
            View and manage all sales transactions, track payments, and monitor outstanding balances.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex-none space-x-3">
          {canProcessOrderSales() && pendingOrderConsignments.length > 0 && (
            <button
              type="button"
              onClick={() => setShowOrderConsignmentsModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
              Process Orders ({pendingOrderConsignments.length})
            </button>
          )}
          <button
            type="button"
            onClick={() => handleExport('excel')}
            className="btn-secondary"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            Export
          </button>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          {(user?.user_type === 'sales_agent' || user?.user_type === 'owner') && (
            <button
              type="button"
              onClick={() => navigate('/sales/create')}
              className="btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              New Sale
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-7">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Total Sales</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">
              ZMW {summary.total_sales.toLocaleString()}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Collected</dt>
            <dd className="mt-1 text-2xl font-semibold text-green-600">
              ZMW {summary.total_collected.toLocaleString()}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Outstanding</dt>
            <dd className="mt-1 text-2xl font-semibold text-red-600">
              ZMW {summary.outstanding.toLocaleString()}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Discounts</dt>
            <dd className="mt-1 text-2xl font-semibold text-orange-600">
              ZMW {summary.total_discount.toLocaleString()}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Commission</dt>
            <dd className="mt-1 text-2xl font-semibold text-blue-600">
              ZMW {summary.total_commission.toLocaleString()}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Cash Sales</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">
              ZMW {summary.cash_sales.toLocaleString()}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <dt className="text-sm font-medium text-gray-500 truncate">Credit Sales</dt>
            <dd className="mt-1 text-2xl font-semibold text-orange-600">
              ZMW {summary.credit_sales.toLocaleString()}
            </dd>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mt-6 bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Filter Transactions</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Payment Type</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.payment_type}
                onChange={(e) => setFilters({...filters, payment_type: e.target.value})}
              >
                <option value="">All</option>
                <option value="cash">Cash</option>
                <option value="credit">Credit</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Shop Owner</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.shop_owner_id}
                onChange={(e) => setFilters({...filters, shop_owner_id: e.target.value})}
              >
                <option value="">All</option>
                {shopOwners.map(owner => (
                  <option key={owner.id} value={owner.profile?.id}>
                    {owner.profile?.business_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Sales Agent</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={filters.sales_agent_id}
                onChange={(e) => setFilters({...filters, sales_agent_id: e.target.value})}
              >
                <option value="">All</option>
                {salesAgents.map(agent => (
                  <option key={agent.id} value={agent.profile?.id}>
                    {agent.full_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">From Date</label>
              <DatePicker
                selected={filters.from_date}
                onChange={date => setFilters({...filters, from_date: date})}
                selectsStart
                startDate={filters.from_date}
                endDate={filters.to_date}
                maxDate={filters.to_date}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                dateFormat="yyyy-MM-dd"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">To Date</label>
              <DatePicker
                selected={filters.to_date}
                onChange={date => setFilters({...filters, to_date: date})}
                selectsEnd
                startDate={filters.from_date}
                endDate={filters.to_date}
                minDate={filters.from_date}
                maxDate={new Date()}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                dateFormat="yyyy-MM-dd"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={fetchTransactions}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Transactions Table */}
      <div className="mt-8">
        <DataTable
          data={transactions}
          columns={columns}
          loading={loading}
          pageSize={pagination.per_page}
          total={pagination.total}
          page={pagination.page}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onPageSizeChange={(per_page) => setPagination(prev => ({ ...prev, per_page, page: 1 }))}
          onExport={handleExport}
        />
      </div>

      {/* Payment Modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setPaymentData({
            amount: '',
            payment_method: 'cash',
            reference_number: '',
            notes: ''
          });
        }}
        title={`Add Payment - ${selectedTransaction?.transaction_number}`}
      >
        <form onSubmit={handleAddPayment}>
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="text-lg font-medium">ZMW {selectedTransaction?.total_amount?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Amount Paid</p>
                  <p className="text-lg font-medium text-green-600">ZMW {selectedTransaction?.amount_paid?.toLocaleString()}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Balance</p>
                  <p className="text-2xl font-bold text-red-600">ZMW {selectedTransaction?.balance?.toLocaleString()}</p>
                </div>
                {selectedTransaction?.commission_amount > 0 && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600">Agent Commission</p>
                    <p className="text-lg font-medium text-blue-600">ZMW {selectedTransaction?.commission_amount?.toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="form-label">Payment Amount *</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={selectedTransaction?.balance}
                required
                className="form-input"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({...paymentData, amount: parseFloat(e.target.value)})}
              />
            </div>

             {(paymentData.amount > 0 && selectedTransaction?.balance?.toFixed(2) > paymentData.amount) && (
                                          <div className="col-span-6 sm:col-span-3">
                                            <label className="block text-sm font-medium text-gray-700">
                                              Due Date *
                                            </label>
                                            <input
                                              type="date"
                                              required
                                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                              value={paymentData.due_date}
                                              onChange={(e) => setPaymentData({...paymentData, due_date: e.target.value})}
                                              min={format(new Date(), 'yyyy-MM-dd')}
                                            />
                                          </div>
                                        )}

            <div>
              <label className="form-label">Payment Method *</label>
              <select
                required
                className="form-input"
                value={paymentData.payment_method}
                onChange={(e) => setPaymentData({...paymentData, payment_method: e.target.value})}
              >
                <option value="cash">Cash</option>
                <option value="mobile_money">Mobile Money</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="check">Check</option>
              </select>
            </div>

            <div>
              <label className="form-label">Reference Number</label>
              <input
                type="text"
                className="form-input"
                readOnly
                value={selectedTransaction?.transaction_number}
                //value={paymentData.reference_number}
                //onChange={(e) => setPaymentData({...paymentData, reference_number: e.target.value})}
              />
            </div>

            <div>
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                rows="2"
                value={paymentData.notes}
                onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentData({
                    amount: '',
                    payment_method: 'cash',
                    reference_number: '',
                    notes: ''
                  });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Add Payment
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Items Details Modal */}
      <Modal
        isOpen={showItemsModal}
        onClose={() => {
          setShowItemsModal(false);
          setSelectedTransaction(null);
        }}
        title={`Items - ${selectedTransaction?.transaction_number}`}
        size="lg"
      >
        {selectedTransaction && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Subtotal</p>
                  <p className="text-lg font-medium">ZMW {selectedTransaction.subtotal?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Discount</p>
                  <p className="text-lg font-medium text-red-600">-ZMW {selectedTransaction.discount?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-lg font-bold text-primary-600">ZMW {selectedTransaction.total_amount?.toLocaleString()}</p>
                </div>
                {selectedTransaction.commission_amount > 0 && (
                  <div className="col-span-3">
                    <p className="text-sm text-gray-600">Commission</p>
                    <p className="text-lg font-medium text-blue-600">ZMW {selectedTransaction.commission_amount?.toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cases</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedTransaction.item_summary?.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-900">{item.product_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.quantity_cases}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">ZMW {item.unit_price.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">ZMW {item.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>

      {/* Pending Order Consignments Modal - FIXED */}
      <Modal
        isOpen={showOrderConsignmentsModal}
        onClose={() => setShowOrderConsignmentsModal(false)}
        title="Pending Order Deliveries"
        size="xl"
      >
        <div className="space-y-4">
          {pendingOrderConsignments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ShoppingCartIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p>No pending order deliveries</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Consignment #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Order #
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Items
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Total
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingOrderConsignments.map((consignment) => {
                    // Find the first pending order (not completed)
                    const pendingOrder = consignment.linked_orders?.find(o => o.status !== 'completed');
                    if (!pendingOrder) return null;
                    
                    return (
                      <tr key={consignment.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-primary-600">
                          {consignment.consignment_number}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {pendingOrder.shop_owner_name || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {pendingOrder.order_number || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {consignment.items_count} items
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium">
                          ZMW {pendingOrder.total_amount?.toLocaleString() || 0}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleProcessOrderSale(consignment)}
                            disabled={
                              processingSale &&
                              processingConsignment?.id === consignment.id
                            }
                            className="btn-primary text-sm py-1 px-3"
                          >
                            {processingSale &&
                            processingConsignment?.id === consignment.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                              <>
                                <CurrencyDollarIcon className="h-4 w-4 mr-1 inline" />
                                Complete Sale
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
               </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Sales;